import { useState, useCallback, useEffect } from 'react';
import { Card, Text } from '@nimbus-ds/components';
import { AddressingConfigurationRuleResponseDto } from '@tiendanube/common';
import { CancelAndConfirmButtons, Stack } from 'commons/components';
import { useAddressingConfigurationRulesManagement } from 'domains/Shipping/DeliveryMethods/hooks';
import {
  trackingShippingChangeAddressingRuleV2,
  trackingShippingEditAddressingRules,
} from 'domains/Shipping/tracking';
import useTranslationShipping from 'domains/Shipping/useTranslationShipping';
import './RulesEditBodyAndFooter.scss';
import AppRuleBox from './AppRuleBox';
import RuleSelector from './RuleSelector';
import {
  ADDRESSING_RULES_TYPES,
  AddressingRuleType,
  TIEBREAKER_RULES_TYPES,
} from '../constants';
import {
  changeAppRule,
  changeTiebreakerRule,
  findRuleByType,
  getEnabledRules,
} from '../utils';

interface RulesEditBodyAndFooterProps {
  handleStopEditing: () => void;
  openLocationsPriorityModal: () => void;
}

function RulesEditBodyAndFooter({
  handleStopEditing,
  openLocationsPriorityModal,
}: Readonly<RulesEditBodyAndFooterProps>) {
  const { Body, Footer } = Card;
  const t = useTranslationShipping(
    'deliveryMethods.addressingConfigurationV2.info',
  );

  const { rules, saveStatus, saveConfigurationRules } =
    useAddressingConfigurationRulesManagement();

  const [isDirty, setIsDirty] = useState(false);

  const [editingRules, setEditingRules] =
    useState<AddressingConfigurationRuleResponseDto[]>(rules);

  const materializeRuleEnabled = findRuleByType(
    editingRules,
    ADDRESSING_RULES_TYPES.MATERIALIZE,
  )?.enabled;

  const hasEnabledRules = editingRules.some((rule) => rule.enabled);

  const handleChangeRule = useCallback(
    (value: boolean, type: AddressingRuleType, appCode?: string) => {
      let newRules = editingRules.map((rule) =>
        rule.type === ADDRESSING_RULES_TYPES[type] ||
        (type === ADDRESSING_RULES_TYPES.APP && rule.code === appCode)
          ? { ...rule, enabled: value }
          : rule,
      );

      if (TIEBREAKER_RULES_TYPES.includes(type)) {
        newRules = changeTiebreakerRule(
          newRules,
          value,
          type === ADDRESSING_RULES_TYPES.CD_PRIORITY,
        );
      }

      if (type === ADDRESSING_RULES_TYPES.APP) {
        newRules = changeAppRule(newRules, appCode);
      }

      setEditingRules(newRules);

      trackingShippingChangeAddressingRuleV2(editingRules, type);
      setIsDirty(true);
    },
    [editingRules],
  );

  const handleSaveRules = useCallback(async () => {
    const enabledRules = getEnabledRules(editingRules);

    await saveConfigurationRules(enabledRules);
    handleStopEditing();
  }, [editingRules, saveConfigurationRules, handleStopEditing]);

  const stopEditing = useCallback(
    (action: 'cancel' | 'save') => {
      trackingShippingEditAddressingRules(editingRules, action);
      handleStopEditing();
    },
    [editingRules, handleStopEditing],
  );

  useEffect(() => {
    setEditingRules(rules);
  }, [rules]);

  return (
    <>
      <Body>
        <Stack column spacing="base" align="stretch">
          <Text fontSize="base" lineHeight="base" textAlign="left">
            {t('description')}
          </Text>
          <Stack align="stretch" column>
            {editingRules.filter(
              (rule) => rule.type === ADDRESSING_RULES_TYPES.APP,
            ).length > 0 && (
              <AppRuleBox
                rules={editingRules.filter(
                  (rule) => rule.type === ADDRESSING_RULES_TYPES.APP,
                )}
                handleChangeRule={handleChangeRule}
              />
            )}
            <Stack align="flex-start" spacing="tight" column>
              <Text color="neutral-textHigh" fontWeight="bold">
                1. {t('principalRule')}
              </Text>
              <RuleSelector
                rules={editingRules}
                type="materialize"
                handleChangeRule={handleChangeRule}
              />
            </Stack>
            <Stack align="flex-start" spacing="tight" column>
              <Text color="neutral-textHigh" fontWeight="bold">
                2.{' '}
                {t(
                  `secondaryRule.${
                    materializeRuleEnabled
                      ? 'withMaterialize'
                      : 'withoutMaterialize'
                  }`,
                )}
              </Text>
              <RuleSelector
                rules={editingRules}
                type="tiebreaker"
                handleChangeRule={handleChangeRule}
                openLocationsPriorityModal={openLocationsPriorityModal}
              />
            </Stack>
          </Stack>
        </Stack>
      </Body>
      <Footer>
        <CancelAndConfirmButtons
          onCancel={() => stopEditing('cancel')}
          onConfirm={handleSaveRules}
          isLoading={saveStatus === 'loading'}
          isConfirmDisabled={!isDirty || !hasEnabledRules}
        />
      </Footer>
    </>
  );
}

export default RulesEditBodyAndFooter;
